import { useState } from "react";
import { motion } from "framer-motion";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Reveal, MaskLinesInView } from "./motion";

const A = "https://customer-assets.emergentagent.com/job_montebello-lodge/artifacts";
const B = "https://customer-assets-lxgj4vgw.emergentagent.net/job_montebello-lodge/artifacts";

// Exteriores — casa, jardins e floresta
const EXTERIORS = [
  { src: `${A}/ri9bhuvk_image.png`, alt: "Fachada da Casa Montebello com a bandeira portuguesa" },
  { src: `${B}/mgwczhop_WhatsApp%20Image%202026-07-27%20at%2011.25.11%20%282%29.jpeg`, alt: "Copa de árvore centenária vista de baixo" },
  { src: `${B}/7hn17wku_WhatsApp%20Image%202026-07-27%20at%2011.25.13%20%284%29.jpeg`, alt: "Ribeiro e lago rodeados de fetos no jardim" },
  { src: `${B}/e948x5ym_WhatsApp%20Image%202026-07-27%20at%2011.25.13%20%286%29.jpeg`, alt: "Fonte de pedra em forma de dragão coberta de musgo" },
  { src: `${B}/v0u95xke_WhatsApp%20Image%202026-07-27%20at%2011.25.13%20%281%29.jpeg`, alt: "Caminho de bambus com mesa de pedra" },
  { src: `${B}/26231kt5_WhatsApp%20Image%202026-07-27%20at%2011.25.11%20%285%29.jpeg`, alt: "Fonte de pedra coberta de musgo no bambuzal" },
  { src: `${B}/fj4oedl7_WhatsApp%20Image%202026-07-27%20at%2011.25.13%20%287%29.jpeg`, alt: "Casinha de madeira no meio da floresta" },
  { src: `${B}/28654b4fe3bd23b3_926208002.jpg`, alt: "Terraço coberto com mesas corridas para eventos" },
  { src: `${B}/9222325ae2672b98_926208007.jpg`, alt: "Churrasqueira em tijolo com telheiro no jardim" },
];

// Interiores — entrada, salas, cozinha, casa de banho e quartos
const INTERIORS = [
  { src: `${B}/r39u9qph_WhatsApp%20Image%202026-07-27%20at%2011.25.09%20%283%29.jpeg`, alt: "Escadaria com corrimão de madeira e tapete clássico" },
  { src: `${B}/blv6ky8b_WhatsApp%20Image%202026-07-27%20at%2011.25.11%20%284%29.jpeg`, alt: "Entrada com portas envidraçadas e corredor da casa" },
  { src: `${B}/tqh15pyn_WhatsApp%20Image%202026-07-27%20at%2011.25.12%20%282%29.jpeg`, alt: "Corredor com escadaria em madeira e candeeiro clássico" },
  { src: `${B}/2v1p2o7c_WhatsApp%20Image%202026-07-27%20at%2011.25.14%20%282%29.jpeg`, alt: "Sala de estar verde com lareira e chandelier" },
  { src: `${B}/vzo09j05_WhatsApp%20Image%202026-07-27%20at%2011.25.09%20%282%29.jpeg`, alt: "Sala de estar com teto trabalhado, lareira e sofá" },
  { src: `${A}/aew5gmky_image.png`, alt: "Sala de estar clássica com lareira e chandelier" },
  { src: `${B}/e72qcp1l_WhatsApp%20Image%202026-07-27%20at%2011.25.09%20%284%29.jpeg`, alt: "Sala de jantar com mesas e toalhas aos quadrados azuis" },
  { src: `${B}/7hcci1lf_WhatsApp%20Image%202026-07-27%20at%2011.25.10.jpeg`, alt: "Cozinha ampla com lareira, azulejos e mesa comprida" },
  { src: `${B}/z464miq0_WhatsApp%20Image%202026-07-27%20at%2011.25.12%20%281%29.jpeg`, alt: "Casa de banho clássica com banheira de patas e torneiras douradas" },
  { src: `${B}/938680b84045a348_926637591.jpg`, alt: "Quarto amarelo com cama de casal em pinho e varanda" },
  { src: `${B}/9fe1dfa317738c75_926637755.jpg`, alt: "Quarto amarelo com cama de casal em madeira escura e candeeiro de teto" },
  { src: `${B}/656c53cf5a352e48_926637636.jpg`, alt: "Quarto amarelo com duas camas individuais em madeira escura" },
  { src: `${B}/a8fec0b40a4eb45f_926637730.jpg`, alt: "Quarto amarelo com duas camas individuais em pinho e candeeiro de teto" },
  { src: `${B}/c163df06bbaab1d5_926637840.jpg`, alt: "Quarto azul com cama individual e janela com vista para o jardim" },
  { src: `${B}/e0d15b80fa34ad67_926207998.jpg`, alt: "Cozinha profissional em inox e pedra para eventos" },
];

const EASE = [0.22, 1, 0.36, 1];

function MasonryGroup({ images, onOpen, offset = 0 }) {
  return (
    <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 lg:gap-5 [column-fill:_balance]">
      {images.map((img, i) => (
        <motion.button
          key={img.src}
          data-testid={`gallery-item-${offset + i}`}
          onClick={() => onOpen(img)}
          initial={{ opacity: 0, y: 24, filter: "blur(6px)" }}
          whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          viewport={{ once: true, margin: "-6%" }}
          transition={{ duration: 0.8, ease: EASE, delay: (i % 3) * 0.05 }}
          className="group relative mb-4 lg:mb-5 block w-full overflow-hidden rounded-sm bg-border break-inside-avoid"
        >
          <img
            src={img.src}
            alt={img.alt}
            className="w-full h-auto object-cover transition-transform duration-[1400ms] group-hover:scale-105"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-forest/0 group-hover:bg-forest/15 transition-colors" />
        </motion.button>
      ))}
    </div>
  );
}

export default function Gallery() {
  const [active, setActive] = useState(null);

  return (
    <section id="galeria" data-testid="gallery-section" className="relative py-28 lg:py-40 bg-cream">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14 lg:mb-20">
          <div className="max-w-2xl">
            <Reveal>
              <div className="flex items-center gap-4 mb-8">
                <span className="h-px w-14 bg-forest/40" />
                <p className="overline">Galeria</p>
              </div>
            </Reveal>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-[4rem] leading-[1.05] tracking-tight text-forest font-light">
              <MaskLinesInView lines={["A casa e o que a", <span key="i" className="italic">rodeia.</span>]} />
            </h2>
          </div>
          <Reveal delay={0.1}>
            <p className="max-w-md text-muted-foreground leading-relaxed">
              A propriedade real — a casa, o bambuzal, os ribeiros e a floresta privada, e o interior clássico de várias salas e quartos. Toque em qualquer imagem para ver em ecrã cheio.
            </p>
          </Reveal>
        </div>

        <MasonryGroup images={EXTERIORS} onOpen={setActive} offset={0} />
        <div className="h-8 lg:h-12" />
        <MasonryGroup images={INTERIORS} onOpen={setActive} offset={EXTERIORS.length} />
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent data-testid="gallery-lightbox" className="max-w-6xl bg-transparent border-none p-0 shadow-none">
          <DialogTitle className="sr-only">Imagem da galeria</DialogTitle>
          {active && (
            <img src={active.src} alt={active.alt} className="w-full max-h-[85vh] object-contain rounded-sm" />
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
